import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAtomValue, useSetAtom } from "jotai";
import { userTypeAtom, updateAuthStateAtom } from "../atoms/authAtoms";
import { authService } from "../services/auth";
import { locationDestination, postAuthDestination } from "../utils/reviewerNav";
import { FaSpinner } from "../utils/icons";
import { Alert, AlertDescription, Button } from "@/design-system/components/react";
import {
  ANNOTATOR_AGREEMENT_TITLE,
  ANNOTATOR_AGREEMENT_PREAMBLE,
  ANNOTATOR_AGREEMENT_SECTIONS,
  ANNOTATOR_AGREEMENT_VERSION,
} from "../constants/annotator-agreement";
import {
  DEMO_VIEWER_AGREEMENT_TITLE,
  DEMO_VIEWER_AGREEMENT_PREAMBLE,
  DEMO_VIEWER_AGREEMENT_SECTIONS,
  DEMO_VIEWER_AGREEMENT_VERSION,
} from "../constants/demo-viewer-agreement";
import {
  ORG_AGREEMENT_ACK_TITLE,
  ORG_AGREEMENT_ACK_PREAMBLE,
  ORG_AGREEMENT_ACK_SECTIONS,
  ORG_AGREEMENT_ACK_VERSION,
} from "../constants/org-agreement-ack";

interface AgreementContent {
  title: string;
  preamble: string;
  sections: { heading: string; body: string }[];
  consent: string;
}

const AGREE_TO_TERMS =
  'By clicking "I Accept", you acknowledge that you have read, understood, and agree to the terms above.';

/** Content for each agreement the backend can require, keyed by its version. */
const AGREEMENTS: Record<string, AgreementContent> = {
  [ANNOTATOR_AGREEMENT_VERSION]: {
    title: ANNOTATOR_AGREEMENT_TITLE,
    preamble: ANNOTATOR_AGREEMENT_PREAMBLE,
    sections: ANNOTATOR_AGREEMENT_SECTIONS,
    consent: AGREE_TO_TERMS,
  },
  [DEMO_VIEWER_AGREEMENT_VERSION]: {
    title: DEMO_VIEWER_AGREEMENT_TITLE,
    preamble: DEMO_VIEWER_AGREEMENT_PREAMBLE,
    sections: DEMO_VIEWER_AGREEMENT_SECTIONS,
    consent: AGREE_TO_TERMS,
  },
  [ORG_AGREEMENT_ACK_VERSION]: {
    title: ORG_AGREEMENT_ACK_TITLE,
    preamble: ORG_AGREEMENT_ACK_PREAMBLE,
    sections: ORG_AGREEMENT_ACK_SECTIONS,
    consent: 'By clicking "I Accept", you confirm the above.',
  },
};

const AgreementGate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userType = useAtomValue(userTypeAtom);
  const updateAuthState = useSetAtom(updateAuthStateAtom);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The backend names the agreement at sign-in (role click-through, or the
  // company's acknowledgement). A session from before it did so has none:
  // ask for a fresh sign-in rather than guess.
  const version = authService.getStoredAuth().agreementType;
  const agreement = version ? AGREEMENTS[version] : undefined;

  // Where to go after accepting. Honour the path the user was originally
  // headed to; otherwise route by user_type.
  const fromState = (location.state as any)?.from;
  const destination = locationDestination(fromState) ?? postAuthDestination({ userType });

  // NOTE: agreement_version comes from a cookie the frontend controls. The
  // backend MUST validate that the submitted version matches the agreement
  // it resolves for the user, so a tampered cookie cannot accept the wrong one.
  const handleAccept = async () => {
    if (!version) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await authService.acceptAgreement(version);
      // Flip the in-process atom so PrivateRoute stops bouncing this session;
      // the cookie has already been set by acceptAgreement for next-reload.
      updateAuthState({ agreementAccepted: true });
      navigate(destination, { replace: true });
    } catch (err: unknown) {
      const detail =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      setError(detail || "Failed to accept agreement.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!agreement) {
    return (
      <div className="flex min-h-0 flex-1 bg-bg-2 items-start justify-center p-4 py-8">
        <Alert variant="destructive" className="max-w-2xl p-3 bg-warn-tint">
          <AlertDescription>Please sign out and sign in again to continue.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 bg-bg-2 text-white items-start justify-center overflow-auto p-4 py-8">
      <div className="w-full max-w-2xl bg-surface-2 backdrop-blur-md border border-border-strong rounded-lg p-8 shadow-2xl">
        <div className="text-center mb-6">
          <span className="text-2xl font-display font-bold tracking-[-0.04em] text-fg">
            a2z<span className="text-signal-text-tint">.</span>
          </span>
          <h2 className="mt-4 text-xl font-medium text-fg-muted">{agreement.title}</h2>
          <p className="mt-4 text-sm text-fg-muted leading-relaxed">{agreement.preamble}</p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6 p-3 bg-warn-tint">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-6 mb-8">
          {agreement.sections.map((section, i) => (
            <div key={i}>
              <h3 className="text-sm font-semibold text-fg-muted mb-1">
                {i + 1}. {section.heading}
              </h3>
              <p className="text-sm text-fg-muted leading-relaxed">{section.body}</p>
            </div>
          ))}
        </div>

        <div className="border-t border-border-strong pt-6">
          <p className="text-sm text-fg-muted mb-4">{agreement.consent}</p>
          <Button onClick={handleAccept} disabled={isSubmitting} className="w-full">
            {isSubmitting ? (
              <>
                <FaSpinner className="animate-spin mr-2 h-4 w-4" />
                <span>Accepting...</span>
              </>
            ) : (
              <span>I Accept</span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AgreementGate;
